import { FC } from 'react';
import { useIntl } from 'react-intl';
import { CheckmarkCircleOutlinedIcon } from '@getgo/chameleon-icons/react';
import { Button, ButtonLink, Dialog, MenuSeparator, Typography } from '@getgo/chameleon-web-react-wrapper';

import config from 'config';
import { useAppSelector } from 'hooks';
import { isProcessRenewalSuccessful } from 'modules/cog/cog-selector';
import { accountKey } from 'modules/global-wrapper';
import { effectiveBillingRoles } from 'modules/iam/iam-selectors';
import {
  isEcomClient,
  sessionInvoiceNumber,
  sessionProducts,
  sessionSuccessRedirectUrl,
  sesssionCancelRedirectUrl,
} from 'modules/session-details';
import Track, { GAEventNames } from 'modules/tracking';
import { isProcessInvoiceSuccessful, isProcessQuoteSuccessful } from 'modules/ucs/ucs-selector';
import { classicProductFamilyKey, PARTNER_TYPES, SEARCH_PARAMS } from 'utils/constants';
import st from 'utils/shared-translations';

import './feedback.css';

const PaymentSuccess: FC = (): JSX.Element => {
  const intl = useIntl();

  const partnerType = localStorage.getItem(SEARCH_PARAMS.partnerType);
  const selectedAccountKey = useAppSelector(accountKey);

  const selectedIsProcessQuoteSucceed = useAppSelector(isProcessQuoteSuccessful);
  const selectedIsProcessRenewalSucceed = useAppSelector(isProcessRenewalSuccessful);
  const selectedIsProcessInvoiceSucceed = useAppSelector(isProcessInvoiceSuccessful);

  const selectedSuccessRedirectUrl = useAppSelector(sessionSuccessRedirectUrl);
  const selectedCancelRedirectUrl = useAppSelector(sesssionCancelRedirectUrl);
  const selectedQuotePriceList = useAppSelector(sessionProducts);
  const selectedInvoiceNumber = useAppSelector(sessionInvoiceNumber);

  const selectedClientIsEcom = useAppSelector(isEcomClient);

  const selectedEffectiveBillingRoles = useAppSelector(effectiveBillingRoles);

  const isClassicProductFamily = selectedQuotePriceList.some((item) =>
    classicProductFamilyKey.includes(item?.productGroup),
  );

  let ctaName = 'feedback.success.back.to.quotes';
  if (selectedClientIsEcom) {
    ctaName = 'feedback.success.cta.continue';
  } else if (selectedInvoiceNumber) {
    ctaName = 'feedback.success.back.to.invoices';
  } else if (selectedEffectiveBillingRoles?.includes('VIEW_SUBSCRIPTION')) {
    ctaName = 'feedback.success.back.to.subscriptions';
  }

  const goToSuccessURL = () => {
    localStorage.clear();
    Track(GAEventNames.ClickCTA, { ctaName: st[ctaName].defaultMessage });
    window.location.assign(selectedSuccessRedirectUrl);
  };

  const hasGrantPartnerAccessPrivilege =
    selectedCancelRedirectUrl.includes('quotes') && partnerType?.toUpperCase() === PARTNER_TYPES.child;

  const displayToProductCTA =
    isClassicProductFamily && !hasGrantPartnerAccessPrivilege && !selectedClientIsEcom && !selectedInvoiceNumber;

  return (
    <Dialog
      open={selectedIsProcessQuoteSucceed || selectedIsProcessRenewalSucceed || selectedIsProcessInvoiceSucceed}
      closable={false}
      size="large"
      actions={
        <div className="feedback__ctas">
          {displayToProductCTA && (
            <ButtonLink variant="tertiary" size="medium" href={config.CLASSIC_PRODUCTS_URL} target="_blank">
              {intl.formatMessage(st['feedback.success.to.product'])}
            </ButtonLink>
          )}
          {hasGrantPartnerAccessPrivilege && (
            <ButtonLink
              variant="tertiary"
              size="medium"
              href={`${config.ADMIN_URL}/${selectedAccountKey}/settings/partner`}
              onClick={() => localStorage.clear()}
            >
              {intl.formatMessage(st['feedback.success.partner.grant.cta'])}
            </ButtonLink>
          )}
          <Button size="medium" variant="primary" onClick={goToSuccessURL}>
            {intl.formatMessage(st[ctaName])}
          </Button>
        </div>
      }
    >
      <div className="feedback__body">
        <CheckmarkCircleOutlinedIcon size="80px" color="var(--goto-icon-color-success)" />
        <Typography tag="h3" variant="heading-small" className="feedback__body-info" gutterBottom>
          {intl.formatMessage(st['feedback.success.order.purchase.successful'])}
        </Typography>
        <MenuSeparator className="feedback__menu-separator" />
      </div>
    </Dialog>
  );
};

export default PaymentSuccess;
